/*
 * @format
 */

import React from 'react';

import WatchFace, { FACE_RADIUS } from './WatchFace';
import { Arrow, DiagramFrame, Label, TapDot, TAP_RADIUS } from './DiagramParts';

/*
 * The same watch face, annotated with the gestures that move around it rather
 * than with what it is showing: a swipe across, a tap, and a swipe up.
 */
const WIDTH = 360;
const HEIGHT = 452;
const CX = WIDTH / 2;
const CY = 232;

// The first caption starts here, and everything else follows from it: the frame
// is only as tall as the drawing needs, so the page's own spacing above and
// below is the only gap the reader sees.
const TOP = 26;
const SWIPE_LEFT_ARROW_Y = 94;
const SWIPE_UP_LABEL_Y = 382;
// The caption is ranged left, placed so its heading — "Swipe up", 78 wide at
// LABEL_SIZE in bold — centres on the face, with the arrow clear of the longest
// line beneath it.
const SWIPE_UP_LABEL_X = CX - 39;
const SWIPE_UP_ARROW_X = 327;
const SWIPE_UP_ARROW = {
  from: { x: SWIPE_UP_ARROW_X, y: 434 },
  to: { x: SWIPE_UP_ARROW_X, y: 372 },
};

// How far the swipe arrow reaches either side of the face's centre — a little
// inside the face, so it reads as a gesture across the screen.
const SWIPE_ARROW_REACH = FACE_RADIUS - 24;

// The tap indicator sits below the face's centre, so its clearance is measured
// from where the face's edge actually is at that height, not from its widest
// point.
const TAP_DROP = 50;
const TAP_CLEARANCE = 18;
const TAP = {
  x:
    CX -
    Math.sqrt(FACE_RADIUS ** 2 - TAP_DROP ** 2) -
    TAP_CLEARANCE -
    TAP_RADIUS,
  y: CY + TAP_DROP,
};

// The caption hangs off the indicator rather than off the frame, so the two
// read as one annotation: ranged left of it, and clear of the shadow below.
const TAP_LABEL_INSET = 20;
const TAP_LABEL = {
  x: TAP.x - TAP_RADIUS - TAP_LABEL_INSET,
  y: TAP.y + TAP_RADIUS + 24,
};

function WatchNavigationDiagram() {
  return (
    <DiagramFrame
      width={WIDTH}
      height={HEIGHT}
      description={
        'The gestures that move around the watch app: swipe left to move ' +
        'between zones, tap to enter the menu or save a change, and swipe up ' +
        'to move between properties.'
      }>
      {/* Ranged left off the arrow beneath it, so caption and gesture start
          from the same edge. */}
      <Label
        x={CX - SWIPE_ARROW_REACH}
        y={TOP}
        bold
        heading="Swipe left"
        body={['Navigate between zones']}
      />
      <Arrow
        from={{ x: CX + SWIPE_ARROW_REACH, y: SWIPE_LEFT_ARROW_Y }}
        to={{ x: CX - SWIPE_ARROW_REACH, y: SWIPE_LEFT_ARROW_Y }}
      />

      <WatchFace cx={CX} cy={CY} />

      <TapDot x={TAP.x} y={TAP.y} />
      <Label
        x={TAP_LABEL.x}
        y={TAP_LABEL.y}
        bold
        heading="Tap"
        body={['Enter menu or', 'save changes']}
      />

      <Label
        x={SWIPE_UP_LABEL_X}
        y={SWIPE_UP_LABEL_Y}
        bold
        heading="Swipe up"
        body={['Navigate between', 'multiple properties, if', 'applicable']}
      />
      <Arrow from={SWIPE_UP_ARROW.from} to={SWIPE_UP_ARROW.to} />
    </DiagramFrame>
  );
}

export default WatchNavigationDiagram;
